// P7 (2026-11-02) — Onboarding checklist for new customer orgs.
//
// 3 steps: brand basics → connect channels → first approved post.
// Owner-only writes; any member can see progress. The page also
// surfaces the per-org platform-approval ceiling so customer orgs
// see "TikTok: SELF_ONLY — audit pending" rather than being surprised
// at publish time.

import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";

import Shell from "../layout/Shell";
import { api, formatApiError } from "../lib/api";

export default function Onboarding() {
  const [state, setState] = useState({ loading: true, error: null });
  const [data, setData] = useState(null);
  const [approvals, setApprovals] = useState(null);
  const [busy, setBusy] = useState(null);

  const load = useCallback(async () => {
    try {
      const [r1, r2] = await Promise.all([
        api.get("/onboarding"),
        api.get("/platform-approval-status"),
      ]);
      setData(r1.data || null);
      setApprovals(r2.data || null);
      setState({ loading: false, error: null });
    } catch (e) {
      setState({ loading: false, error: formatApiError(e) });
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  async function complete(step_id) {
    setBusy(step_id);
    try {
      const r = await api.post("/onboarding/complete-step", { step_id });
      setData(r.data || null);
    } catch (e) {
      setState((s) => ({ ...s, error: formatApiError(e) }));
    } finally { setBusy(null); }
  }

  return (
    <Shell title="Onboarding">
      <div className="bo-page" data-testid="onboarding-page"
           style={{ padding: 24 }}>
        <div className="bo-eyebrow">Precipice Brand OS</div>
        <h1 className="bo-serif" style={{ fontSize: 26, margin: 0 }}>
          Set up your workspace
        </h1>
        <p style={{ color: "var(--bo-muted)", maxWidth: 640, marginTop: 4 }}>
          Three steps to a first approved post. Each channel connect uses
          your workspace's own OAuth tokens.
        </p>

        {state.error && (
          <div className="bo-card" style={{ padding: 12, color: "crimson" }}
               data-testid="onboarding-error">
            {typeof state.error === "string" ? state.error : JSON.stringify(state.error)}
          </div>
        )}
        {state.loading && (
          <div className="bo-card" style={{ padding: 16 }}>Loading…</div>
        )}

        {data && (
          <div className="bo-card" style={{ padding: 18, marginTop: 16,
                                              display: "grid", gap: 10 }}
               data-testid="onboarding-checklist">
            <div style={{ display: "flex", justifyContent: "space-between" }}>
              <strong>Progress</strong>
              <span data-testid="onboarding-progress-pct">
                {data.progress_pct}%
              </span>
            </div>
            {data.steps.map((s) => (
              <div key={s.id}
                   data-testid={`onboarding-step-${s.id}`}
                   style={{
                     display: "flex", alignItems: "center", gap: 12,
                     padding: 12, borderRadius: 8,
                     border: "1px solid var(--bo-line)",
                     background: s.completed ? "#eaf4ec" : "transparent",
                   }}>
                <div style={{
                  width: 24, height: 24, borderRadius: "50%",
                  background: s.completed ? "#1f6c3f" : "#ddd",
                  color: "#fff", display: "flex",
                  alignItems: "center", justifyContent: "center",
                  fontSize: 13, fontWeight: 700,
                }}>
                  {s.completed ? "✓" : "•"}
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: 600 }}>{s.title}</div>
                  <div style={{ fontSize: 12, color: "var(--bo-muted)" }}>
                    {s.detail}
                  </div>
                </div>
                {!s.completed && (
                  <button
                    className="bo-btn bo-btn-ghost"
                    data-testid={`onboarding-complete-${s.id}`}
                    disabled={busy === s.id}
                    onClick={() => complete(s.id)}
                    style={{ fontSize: 12 }}
                  >
                    {busy === s.id ? "Saving…" : "Mark done"}
                  </button>
                )}
              </div>
            ))}
          </div>
        )}

        {approvals && (
          <div className="bo-card" style={{ padding: 18, marginTop: 16 }}
               data-testid="onboarding-approvals-card">
            <strong>Platform approval ceiling</strong>
            <p style={{ fontSize: 12, color: "var(--bo-muted)" }}>
              Your workspace inherits the app-level approval state while
              each platform's audit is pending.
            </p>
            <ul style={{ fontSize: 13, lineHeight: 1.6 }}>
              {approvals.channels.map((ch) => (
                <li key={ch.channel} data-testid={`approval-${ch.channel}`}>
                  <strong style={{ textTransform: "capitalize" }}>
                    {ch.channel}
                  </strong>
                  {" — "}
                  {ch.app_level.status} · privacy {ch.app_level.privacy}
                </li>
              ))}
            </ul>
          </div>
        )}

        <div style={{ marginTop: 24 }}>
          <Link to="/brand-os" className="bo-btn"
                data-testid="onboarding-to-app"
                style={{ textDecoration: "none" }}>
            Go to Brand OS →
          </Link>
        </div>
      </div>
    </Shell>
  );
}
